import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  FileText, 
  Save,
  RefreshCw,
  Heart,
  Activity,
  Pill,
  AlertTriangle,
  Droplets,
  Scale,
  Ruler,
  User,
  Plus,
  X
} from 'lucide-react';
import { medicalService, getLocalizedValue } from '@/services/api';

const MedicalRecordsPage = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [medicalData, setMedicalData] = useState({
    weight: '',
    height: '',
    bloodGroup: '',
    chronicDiseases: [],
    allergies: [],
    medications: [],
    geneticDiseases: [],
    surgeries: [],
    vaccinations: [],
    smoking: 'no',
    alcohol: 'no',
    notes: '',
  });
  
  // Dropdowns data from API
  const [chronicDiseaseOptions, setChronicDiseaseOptions] = useState([]);
  const [allergyOptions, setAllergyOptions] = useState([]);
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [geneticDiseaseOptions, setGeneticDiseaseOptions] = useState([]);
  const [surgeryOptions, setSurgeryOptions] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  
  const [newAllergy, setNewAllergy] = useState('');
  const [newMedication, setNewMedication] = useState('');
  const [newGeneticDisease, setNewGeneticDisease] = useState('');
  const [newSurgery, setNewSurgery] = useState('');
  const [newVaccination, setNewVaccination] = useState('');

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  
  const smokingOptions = [
    { value: 'no', label: lang === 'ar' ? 'لا' : 'No' },
    { value: 'yes', label: lang === 'ar' ? 'نعم' : 'Yes' },
    { value: 'former', label: lang === 'ar' ? 'سابقاً' : 'Former' },
  ];
  
  const alcoholOptions = [
    { value: 'no', label: lang === 'ar' ? 'لا' : 'No' },
    { value: 'occasional', label: lang === 'ar' ? 'أحياناً' : 'Occasional' },
    { value: 'regular', label: lang === 'ar' ? 'منتظم' : 'Regular' },
  ];

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchMedicalData();
  }, [isAuthenticated, navigate]);

  const fetchMedicalData = async () => {
    setLoading(true);
    try {
      // Fetch all medical record options in parallel
      const [chronicRes, allergiesRes, medicationsRes, geneticRes, surgeriesRes, vaccinesRes] = await Promise.allSettled([
        medicalService.getChronicDiseases(),
        medicalService.getAllergies(),
        medicalService.getPermanentMedications(),
        medicalService.getGeneticDiseases(),
        medicalService.getSurgeries(),
        medicalService.getVaccines(),
      ]);
      
      if (chronicRes.status === 'fulfilled') {
        const data = chronicRes.value?.data?.data || chronicRes.value?.data || [];
        setChronicDiseaseOptions(Array.isArray(data) ? data : []);
      }
      
      if (allergiesRes.status === 'fulfilled') {
        const data = allergiesRes.value?.data?.data || allergiesRes.value?.data || [];
        setAllergyOptions(Array.isArray(data) ? data : []);
      }
      
      if (medicationsRes.status === 'fulfilled') {
        const data = medicationsRes.value?.data?.data || medicationsRes.value?.data || [];
        setMedicationOptions(Array.isArray(data) ? data : []);
      }
      
      if (geneticRes.status === 'fulfilled') {
        const data = geneticRes.value?.data?.data || geneticRes.value?.data || [];
        setGeneticDiseaseOptions(Array.isArray(data) ? data : []);
      }
      
      if (surgeriesRes.status === 'fulfilled') {
        const data = surgeriesRes.value?.data?.data || surgeriesRes.value?.data || [];
        setSurgeryOptions(Array.isArray(data) ? data : []);
      }
      
      if (vaccinesRes.status === 'fulfilled') {
        const data = vaccinesRes.value?.data?.data || vaccinesRes.value?.data || [];
        setVaccineOptions(Array.isArray(data) ? data : []);
      }

      // Try to fetch user's existing medical record
      if (user?.patient_id) {
        try {
          const patientRes = await medicalService.getPatientMedicalRecord(user.patient_id);
          const patient = patientRes?.data?.data || patientRes?.data;
          if (patient) {
            setMedicalData({
              weight: patient.weight || '',
              height: patient.height || '',
              bloodGroup: patient.blood_group || '',
              chronicDiseases: patient.chronic_diseases || patient.patient_chronic_diseases || [],
              allergies: patient.allergies || patient.patient_allergies || [],
              medications: patient.medications || patient.patient_sensitive_to_medication || [],
              geneticDiseases: patient.genetic_diseases || patient.patient_genetic_diseases || [],
              surgeries: patient.surgeries || patient.patient_surgeries || [],
              vaccinations: patient.vaccinations || patient.patient_vaccines || [],
              smoking: patient.smoking || 'no',
              alcohol: patient.alcohol || 'no',
              notes: patient.notes || '',
            });
          }
        } catch (err) {
          console.log('No existing medical record');
        }
      }
    } catch (error) {
      console.error('Failed to fetch medical data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        weight: medicalData.weight,
        height: medicalData.height,
        blood_group: medicalData.bloodGroup,
        chronic_diseases: medicalData.chronicDiseases.map(d => d.id || d),
        allergies: medicalData.allergies.map(a => a.id || a),
        medications: medicalData.medications.map(m => m.id || m),
        notes: medicalData.notes,
      };
      
      if (user?.patient_id) {
        await medicalService.updatePatientMedicalRecord(user.patient_id, payload);
      }
      
      toast.success(lang === 'ar' ? 'تم حفظ السجل الطبي' : 'Medical record saved successfully');
    } catch (error) {
      console.error('Failed to save medical record:', error);
      toast.error(lang === 'ar' ? 'فشل في حفظ السجل الطبي' : 'Failed to save medical record');
    } finally {
      setSaving(false);
    }
  };

  const addChronicDisease = (diseaseId) => {
    const disease = chronicDiseaseOptions.find(d => d.id?.toString() === diseaseId);
    if (disease && !medicalData.chronicDiseases.some(d => d.id === disease.id)) {
      setMedicalData(prev => ({
        ...prev,
        chronicDiseases: [...prev.chronicDiseases, disease]
      }));
    }
  };

  const removeChronicDisease = (diseaseId) => {
    setMedicalData(prev => ({
      ...prev,
      chronicDiseases: prev.chronicDiseases.filter(d => (d.id || d) !== diseaseId)
    }));
  };

  const addAllergy = () => {
    if (newAllergy.trim()) {
      setMedicalData(prev => ({
        ...prev,
        allergies: [...prev.allergies, { id: Date.now(), name: newAllergy }]
      }));
      setNewAllergy('');
    }
  };

  const removeAllergy = (allergyId) => {
    setMedicalData(prev => ({
      ...prev,
      allergies: prev.allergies.filter(a => (a.id || a) !== allergyId)
    }));
  };

  const addMedication = () => {
    if (newMedication.trim()) {
      setMedicalData(prev => ({
        ...prev,
        medications: [...prev.medications, { id: Date.now(), name: newMedication }]
      }));
      setNewMedication('');
    }
  };

  const removeMedication = (medId) => {
    setMedicalData(prev => ({
      ...prev,
      medications: prev.medications.filter(m => (m.id || m) !== medId)
    }));
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="medical-records-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-rose-600 to-rose-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8" />
            <div>
              <h1 className="text-2xl font-bold">
                {lang === 'ar' ? 'السجل الطبي' : 'Medical Record'}
              </h1>
              <p className="text-rose-100 text-sm">
                {lang === 'ar' ? 'معلوماتك الصحية الهامة' : 'Your important health information'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Basic Info */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <User className="h-5 w-5 text-rose-600" />
                  {lang === 'ar' ? 'المعلومات الأساسية' : 'Basic Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 dark:text-slate-300">
                    <Scale className="h-4 w-4" />
                    {lang === 'ar' ? 'الوزن (كجم)' : 'Weight (kg)'}
                  </Label>
                  <Input
                    type="number"
                    value={medicalData.weight}
                    onChange={(e) => setMedicalData(prev => ({ ...prev, weight: e.target.value }))}
                    placeholder="70"
                    className="dark:bg-slate-700 dark:text-white"
                    data-testid="weight-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 dark:text-slate-300">
                    <Ruler className="h-4 w-4" />
                    {lang === 'ar' ? 'الطول (سم)' : 'Height (cm)'}
                  </Label>
                  <Input
                    type="number"
                    value={medicalData.height}
                    onChange={(e) => setMedicalData(prev => ({ ...prev, height: e.target.value }))}
                    placeholder="175"
                    className="dark:bg-slate-700 dark:text-white"
                    data-testid="height-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 dark:text-slate-300">
                    <Droplets className="h-4 w-4" />
                    {lang === 'ar' ? 'فصيلة الدم' : 'Blood Group'}
                  </Label>
                  <Select
                    value={medicalData.bloodGroup}
                    onValueChange={(value) => setMedicalData(prev => ({ ...prev, bloodGroup: value }))}
                  >
                    <SelectTrigger className="dark:bg-slate-700 dark:text-white" data-testid="blood-group-select">
                      <SelectValue placeholder={lang === 'ar' ? 'اختر فصيلة الدم' : 'Select blood group'} />
                    </SelectTrigger>
                    <SelectContent>
                      {bloodGroups.map(group => (
                        <SelectItem key={group} value={group}>{group}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Chronic Diseases */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Heart className="h-5 w-5 text-red-500" />
                  {lang === 'ar' ? 'الأمراض المزمنة' : 'Chronic Diseases'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Select onValueChange={addChronicDisease}>
                  <SelectTrigger className="dark:bg-slate-700 dark:text-white">
                    <SelectValue placeholder={lang === 'ar' ? 'أضف مرض مزمن' : 'Add chronic disease'} />
                  </SelectTrigger>
                  <SelectContent>
                    {chronicDiseaseOptions.map(disease => (
                      <SelectItem key={disease.id} value={disease.id?.toString()}>
                        {getLocalizedValue(disease.name, lang) || disease.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap gap-2">
                  {medicalData.chronicDiseases.map(disease => (
                    <Badge
                      key={disease.id || disease}
                      variant="secondary"
                      className="flex items-center gap-1 px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                    >
                      {getLocalizedValue(disease.name, lang) || disease.name || disease}
                      <button onClick={() => removeChronicDisease(disease.id || disease)}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {medicalData.chronicDiseases.length === 0 && (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {lang === 'ar' ? 'لا توجد أمراض مزمنة مسجلة' : 'No chronic diseases recorded'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Allergies */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  {lang === 'ar' ? 'الحساسية' : 'Allergies'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newAllergy}
                    onChange={(e) => setNewAllergy(e.target.value)}
                    placeholder={lang === 'ar' ? 'أدخل نوع الحساسية' : 'Enter allergy type'}
                    className="dark:bg-slate-700 dark:text-white"
                    onKeyPress={(e) => e.key === 'Enter' && addAllergy()}
                  />
                  <Button onClick={addAllergy} variant="outline" size="icon">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {medicalData.allergies.map(allergy => (
                    <Badge
                      key={allergy.id || allergy}
                      variant="secondary"
                      className="flex items-center gap-1 px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                    >
                      {getLocalizedValue(allergy.name, lang) || allergy.name || allergy}
                      <button onClick={() => removeAllergy(allergy.id || allergy)}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {medicalData.allergies.length === 0 && (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {lang === 'ar' ? 'لا توجد حساسية مسجلة' : 'No allergies recorded'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Medications */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Pill className="h-5 w-5 text-blue-500" />
                  {lang === 'ar' ? 'الأدوية الحساسة' : 'Sensitive Medications'}
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  {lang === 'ar' ? 'الأدوية التي لديك حساسية منها' : 'Medications you are allergic to'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newMedication}
                    onChange={(e) => setNewMedication(e.target.value)}
                    placeholder={lang === 'ar' ? 'أدخل اسم الدواء' : 'Enter medication name'}
                    className="dark:bg-slate-700 dark:text-white"
                    onKeyPress={(e) => e.key === 'Enter' && addMedication()}
                  />
                  <Button onClick={addMedication} variant="outline" size="icon">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {medicalData.medications.map(med => (
                    <Badge
                      key={med.id || med}
                      variant="secondary"
                      className="flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                    >
                      {getLocalizedValue(med.name, lang) || med.name || med}
                      <button onClick={() => removeMedication(med.id || med)}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {medicalData.medications.length === 0 && (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {lang === 'ar' ? 'لا توجد أدوية حساسة مسجلة' : 'No sensitive medications recorded'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Genetic Diseases */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Activity className="h-5 w-5 text-purple-500" />
                  {lang === 'ar' ? 'الأمراض الوراثية' : 'Genetic Diseases'}
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  {lang === 'ar' ? 'الأمراض الوراثية في العائلة' : 'Hereditary diseases in your family'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newGeneticDisease}
                    onChange={(e) => setNewGeneticDisease(e.target.value)}
                    placeholder={lang === 'ar' ? 'أدخل المرض الوراثي' : 'Enter genetic disease'}
                    className="dark:bg-slate-700 dark:text-white"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newGeneticDisease.trim()) {
                        setMedicalData(prev => ({
                          ...prev,
                          geneticDiseases: [...prev.geneticDiseases, newGeneticDisease.trim()]
                        }));
                        setNewGeneticDisease('');
                      }
                    }}
                  />
                  <Button 
                    onClick={() => {
                      if (newGeneticDisease.trim()) {
                        setMedicalData(prev => ({
                          ...prev,
                          geneticDiseases: [...prev.geneticDiseases, newGeneticDisease.trim()]
                        }));
                        setNewGeneticDisease('');
                      }
                    }} 
                    variant="outline" 
                    size="icon"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {medicalData.geneticDiseases.map((disease, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="flex items-center gap-1 px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                    >
                      {typeof disease === 'object' ? (getLocalizedValue(disease.name, lang) || disease.name) : disease}
                      <button onClick={() => {
                        setMedicalData(prev => ({
                          ...prev,
                          geneticDiseases: prev.geneticDiseases.filter((_, i) => i !== index)
                        }));
                      }}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {medicalData.geneticDiseases.length === 0 && (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {lang === 'ar' ? 'لا توجد أمراض وراثية مسجلة' : 'No genetic diseases recorded'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Surgeries */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Activity className="h-5 w-5 text-orange-500" />
                  {lang === 'ar' ? 'العمليات الجراحية' : 'Surgeries'}
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  {lang === 'ar' ? 'العمليات الجراحية السابقة' : 'Previous surgical procedures'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newSurgery}
                    onChange={(e) => setNewSurgery(e.target.value)}
                    placeholder={lang === 'ar' ? 'أدخل اسم العملية' : 'Enter surgery name'}
                    className="dark:bg-slate-700 dark:text-white"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newSurgery.trim()) {
                        setMedicalData(prev => ({
                          ...prev,
                          surgeries: [...prev.surgeries, newSurgery.trim()]
                        }));
                        setNewSurgery('');
                      }
                    }}
                  />
                  <Button 
                    onClick={() => {
                      if (newSurgery.trim()) {
                        setMedicalData(prev => ({
                          ...prev,
                          surgeries: [...prev.surgeries, newSurgery.trim()]
                        }));
                        setNewSurgery('');
                      }
                    }} 
                    variant="outline" 
                    size="icon"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {medicalData.surgeries.map((surgery, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="flex items-center gap-1 px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300"
                    >
                      {typeof surgery === 'object' ? (getLocalizedValue(surgery.name, lang) || surgery.name) : surgery}
                      <button onClick={() => {
                        setMedicalData(prev => ({
                          ...prev,
                          surgeries: prev.surgeries.filter((_, i) => i !== index)
                        }));
                      }}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {medicalData.surgeries.length === 0 && (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {lang === 'ar' ? 'لا توجد عمليات جراحية مسجلة' : 'No surgeries recorded'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Vaccinations */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Activity className="h-5 w-5 text-green-500" />
                  {lang === 'ar' ? 'التطعيمات' : 'Vaccinations'}
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  {lang === 'ar' ? 'التطعيمات التي تلقيتها' : 'Vaccinations you have received'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newVaccination}
                    onChange={(e) => setNewVaccination(e.target.value)}
                    placeholder={lang === 'ar' ? 'أدخل اسم التطعيم' : 'Enter vaccination name'}
                    className="dark:bg-slate-700 dark:text-white"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newVaccination.trim()) {
                        setMedicalData(prev => ({
                          ...prev,
                          vaccinations: [...prev.vaccinations, newVaccination.trim()]
                        }));
                        setNewVaccination('');
                      }
                    }}
                  />
                  <Button 
                    onClick={() => {
                      if (newVaccination.trim()) {
                        setMedicalData(prev => ({
                          ...prev,
                          vaccinations: [...prev.vaccinations, newVaccination.trim()]
                        }));
                        setNewVaccination('');
                      }
                    }} 
                    variant="outline" 
                    size="icon"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {medicalData.vaccinations.map((vaccine, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                    >
                      {typeof vaccine === 'object' ? (getLocalizedValue(vaccine.name, lang) || vaccine.name) : vaccine}
                      <button onClick={() => {
                        setMedicalData(prev => ({
                          ...prev,
                          vaccinations: prev.vaccinations.filter((_, i) => i !== index)
                        }));
                      }}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {medicalData.vaccinations.length === 0 && (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {lang === 'ar' ? 'لا توجد تطعيمات مسجلة' : 'No vaccinations recorded'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Lifestyle - Smoking & Alcohol */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Activity className="h-5 w-5 text-slate-500" />
                  {lang === 'ar' ? 'نمط الحياة' : 'Lifestyle'}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="dark:text-slate-300">
                    {lang === 'ar' ? 'التدخين' : 'Smoking'}
                  </Label>
                  <Select
                    value={medicalData.smoking}
                    onValueChange={(value) => setMedicalData(prev => ({ ...prev, smoking: value }))}
                  >
                    <SelectTrigger className="dark:bg-slate-700 dark:text-white" data-testid="smoking-select">
                      <SelectValue placeholder={lang === 'ar' ? 'اختر' : 'Select'} />
                    </SelectTrigger>
                    <SelectContent>
                      {smokingOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="dark:text-slate-300">
                    {lang === 'ar' ? 'الكحول' : 'Alcohol'}
                  </Label>
                  <Select
                    value={medicalData.alcohol}
                    onValueChange={(value) => setMedicalData(prev => ({ ...prev, alcohol: value }))}
                  >
                    <SelectTrigger className="dark:bg-slate-700 dark:text-white" data-testid="alcohol-select">
                      <SelectValue placeholder={lang === 'ar' ? 'اختر' : 'Select'} />
                    </SelectTrigger>
                    <SelectContent>
                      {alcoholOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Activity className="h-5 w-5 text-teal-500" />
                  {lang === 'ar' ? 'ملاحظات إضافية' : 'Additional Notes'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={medicalData.notes}
                  onChange={(e) => setMedicalData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder={lang === 'ar' ? 'أي معلومات صحية إضافية...' : 'Any additional health information...'}
                  className="min-h-[100px] dark:bg-slate-700 dark:text-white"
                  data-testid="notes-textarea"
                />
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white py-6 text-lg"
              data-testid="save-medical-record-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  {lang === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  {lang === 'ar' ? 'حفظ السجل الطبي' : 'Save Medical Record'}
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicalRecordsPage;
